import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { wibToday } from "@/lib/bizdate";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { rupiah } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calculator } from "lucide-react";

const Row = ({ label, value, bold, testId }) => (
  <div className={`flex justify-between py-1 text-sm ${bold ? "font-bold text-slate-900 border-t pt-2 mt-1" : "text-slate-600"}`} data-testid={testId}>
    <span>{label}</span><span className="tabular-nums">{value}</span>
  </div>
);

export default function HppCalculator() {
  const { can } = useAuth();
  const [variants, setVariants] = useState([]);
  const [variantId, setVariantId] = useState("");
  const [f, setF] = useState({ price_per_kg: "", roll_weight: "", standard_yield: "", reject_pct: 0, manual_override: "", override_reason: "", cost_source: "Manual", effective_from: wibToday() });
  const [full, setFull] = useState(false);
  const [comp, setComp] = useState({ printing_cost: "", packaging_cost: "", overhead_cost: "" });
  const [result, setResult] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/products/variants", { params: { limit: 500 } }).then((r) => setVariants(r.data.items)).catch(() => {}); }, []);

  const onVariant = async (id) => {
    setVariantId(id);
    try {
      const r = await api.get("/costing/resolve-defaults", { params: { variant_id: id } });
      setDefaults(r.data);
      const fp = r.data.fabric_price, yl = r.data.yield;
      setF((p) => ({ ...p, price_per_kg: fp?.price_per_kg ?? "", roll_weight: fp?.default_roll_weight ?? "", standard_yield: yl?.standard_yield ?? "", reject_pct: yl?.reject_pct ?? 0 }));
      setComp({ printing_cost: r.data.printing?.cost_per_pcs ?? "", packaging_cost: r.data.packaging?.cost_per_pcs ?? "", overhead_cost: r.data.overhead?.cost_per_pcs ?? "" });
    } catch (e) { toast.error(apiError(e)); }
  };

  const calc = useCallback(async () => {
    if (!f.price_per_kg || !f.roll_weight || !f.standard_yield) { setResult(null); return; }
    try {
      const body = { price_per_kg: Number(f.manual_override || f.price_per_kg), roll_weight: Number(f.roll_weight), standard_yield: Number(f.standard_yield), reject_pct: Number(f.reject_pct || 0), include_full: full };
      if (full) { body.printing_cost = comp.printing_cost === "" ? null : Number(comp.printing_cost); body.packaging_cost = comp.packaging_cost === "" ? null : Number(comp.packaging_cost); body.overhead_cost = comp.overhead_cost === "" ? null : Number(comp.overhead_cost); }
      const r = await api.post("/costing/calculate", body);
      setResult(r.data);
    } catch (e) { setResult(null); }
  }, [f, full, comp]);

  useEffect(() => { const t = setTimeout(calc, 350); return () => clearTimeout(t); }, [calc]);

  const saveDraft = async () => {
    if (!variantId) return toast.error("Pilih SKU");
    if (!result || result.hpp_dasar == null) return toast.error("HPP Dasar belum lengkap (cek Biaya Jahit & input kain)");
    if (f.manual_override && !f.override_reason.trim()) return toast.error("Isi Alasan Override harga");
    setSaving(true);
    try {
      await api.post("/costing/hpp", {
        variant_id: variantId, cost_source: f.cost_source, reference_price: f.price_per_kg ? Number(f.price_per_kg) : null,
        price_used: Number(f.manual_override || f.price_per_kg), manual_override: f.manual_override ? Number(f.manual_override) : null,
        override_reason: f.override_reason || null, roll_weight: Number(f.roll_weight), standard_yield: Number(f.standard_yield),
        reject_pct: Number(f.reject_pct || 0), include_full: full,
        printing_cost: full && comp.printing_cost !== "" ? Number(comp.printing_cost) : null,
        packaging_cost: full && comp.packaging_cost !== "" ? Number(comp.packaging_cost) : null,
        overhead_cost: full && comp.overhead_cost !== "" ? Number(comp.overhead_cost) : null,
        effective_from: f.effective_from,
      });
      toast.success("Draft HPP dibuat. Ajukan & setujui di Versi HPP.");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <div>
      <PageHeader title="Kalkulator HPP" subtitle="HPP Dasar = Kain + Jahit. HPP Full = + Printing + Packaging + Overhead. Nilai kosong = 'Belum tersedia'." testId="calculator-header" />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div>
            <Label className="text-xs text-slate-500">SKU / Varian</Label>
            <Select value={variantId} onValueChange={onVariant}><SelectTrigger className="mt-1" data-testid="calc-variant"><SelectValue placeholder="Pilih SKU..." /></SelectTrigger>
              <SelectContent>{variants.map((v) => <SelectItem key={v.id} value={v.id}>{v.sku}</SelectItem>)}</SelectContent></Select>
            {defaults && <p className="mt-1 text-[11px] text-slate-400">Harga kain: {defaults.fabric_price ? "dari master" : "Belum tersedia"} · Hasil/roll: {defaults.yield ? "dari master" : "Belum tersedia"} · Jahit: {defaults.sewing_available ? "aktif" : "Belum tersedia"}</p>}
          </div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Kain</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-500">Harga Kain / Kg</Label><Input type="number" className="mt-1" value={f.price_per_kg} onChange={(e) => set("price_per_kg", e.target.value)} data-testid="calc-price" /></div>
            <div><Label className="text-xs text-slate-500">Berat Roll (Kg)</Label><Input type="number" className="mt-1" value={f.roll_weight} onChange={(e) => set("roll_weight", e.target.value)} data-testid="calc-roll" /></div>
            <div><Label className="text-xs text-slate-500">Hasil Standar / Roll (pcs)</Label><Input type="number" className="mt-1" value={f.standard_yield} onChange={(e) => set("standard_yield", e.target.value)} data-testid="calc-yield" /></div>
            <div><Label className="text-xs text-slate-500">Reject %</Label><Input type="number" className="mt-1" value={f.reject_pct} onChange={(e) => set("reject_pct", e.target.value)} data-testid="calc-reject" /></div>
            <div><Label className="text-xs text-slate-500">Override Harga (opsional)</Label><Input type="number" className="mt-1" value={f.manual_override} onChange={(e) => set("manual_override", e.target.value)} data-testid="calc-override" /></div>
            <div><Label className="text-xs text-slate-500">Alasan Override</Label><Input className="mt-1" value={f.override_reason} onChange={(e) => set("override_reason", e.target.value)} /></div>
          </div>
          <div className="flex items-center gap-2 pt-2"><Switch checked={full} onCheckedChange={setFull} data-testid="calc-full-toggle" /><Label className="text-sm">Hitung HPP Full (Printing + Packaging + Overhead)</Label></div>
          {full && (
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs text-slate-500">Printing / Pcs</Label><Input type="number" className="mt-1" value={comp.printing_cost} onChange={(e) => setComp({ ...comp, printing_cost: e.target.value })} data-testid="calc-printing" /></div>
              <div><Label className="text-xs text-slate-500">Packaging / Pcs</Label><Input type="number" className="mt-1" value={comp.packaging_cost} onChange={(e) => setComp({ ...comp, packaging_cost: e.target.value })} data-testid="calc-packaging" /></div>
              <div><Label className="text-xs text-slate-500">Overhead / Pcs</Label><Input type="number" className="mt-1" value={comp.overhead_cost} onChange={(e) => setComp({ ...comp, overhead_cost: e.target.value })} data-testid="calc-overhead" /></div>
            </div>
          )}
          <div><Label className="text-xs text-slate-500">Berlaku Sejak</Label><Input type="date" className="mt-1 w-48" value={f.effective_from} onChange={(e) => set("effective_from", e.target.value)} /></div>
          {can("costing.manage") && <Button onClick={saveDraft} disabled={saving} className="w-full bg-indigo-600 hover:bg-indigo-500" data-testid="save-hpp-draft"><Calculator className="mr-1.5 h-4 w-4" />Simpan sebagai Draft HPP</Button>}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="calc-result">
          <p className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-400">HPP Dasar</p>
          {!result ? <p className="text-sm text-slate-400">Lengkapi input kain untuk melihat hasil.</p> : (
            <>
              <Row label="Harga Kain / Roll" value={rupiah(result.fabric?.harga_roll)} />
              <Row label="Hasil Efektif / Roll" value={result.fabric?.effective_yield} />
              <Row label="Biaya Kain / Pcs" value={rupiah(result.fabric?.fabric_cost_per_pcs)} />
              <Row label="Biaya Jahit / Pcs" value={result.sewing ? rupiah(result.sewing.sewing_per_pcs) : "Belum tersedia"} />
              <Row label="HPP DASAR / PCS" value={result.hpp_dasar != null ? rupiah(result.hpp_dasar) : "Belum lengkap"} bold testId="result-hpp-dasar" />
              {full && (
                <>
                  <p className="mb-2 mt-4 text-sm font-semibold uppercase tracking-wider text-slate-400">HPP Full</p>
                  <Row label="HPP Dasar" value={result.hpp_dasar != null ? rupiah(result.hpp_dasar) : "-"} />
                  <Row label="Printing" value={comp.printing_cost === "" ? "Belum tersedia" : rupiah(comp.printing_cost)} />
                  <Row label="Packaging" value={comp.packaging_cost === "" ? "Belum tersedia" : rupiah(comp.packaging_cost)} />
                  <Row label="Overhead Produksi" value={comp.overhead_cost === "" ? "Belum tersedia" : rupiah(comp.overhead_cost)} />
                  <Row label="HPP FULL / PCS" value={result.hpp_full != null ? rupiah(result.hpp_full) : "Belum lengkap"} bold testId="result-hpp-full" />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
